import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiMonitor,
  FiPlus,
  FiPower,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUserPlus,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import {
  createCounter,
  deleteCounter,
  getAllCounters,
  getCounterStores,
  getCounterUsers,
  searchAssignableCounterUsers,
  assignCounterUser,
  updateCounterUser,
  deactivateCounterUser,
  updateCounter,
} from "../../services/pos";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const emptyForm = {
  storeId: "",
  name: "",
  code: "",
  isActive: true,
};

function CounterModal({
  open,
  onClose,
  onSave,
  counter,
  stores,
  isSaving,
}) {
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;

    setForm({
      storeId: counter?.storeId || "",
      name: counter?.name || "",
      code: counter?.code || "",
      isActive: counter?.isActive ?? true,
    });
    setFormError("");
  }, [open, counter]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.storeId) {
      setFormError("Select a retail store.");
      return;
    }

    if (!form.name.trim()) {
      setFormError("Counter name is required.");
      return;
    }

    await onSave({
      storeId: form.storeId,
      name: form.name.trim(),
      ...(form.code.trim() ? { code: form.code.trim() } : {}),
      ...(counter ? { isActive: form.isActive } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-gray-950/50 p-4 sm:p-6">
      <div className="mx-auto flex min-h-full max-w-2xl items-center">
        <div className="w-full rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-gray-100 p-6">
            <div>
              <p className="text-sm text-gray-500">POS Configuration</p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                {counter ? "Edit POS Counter" : "Create POS Counter"}
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
            >
              <FiX size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {formError && (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="sm:col-span-2">
                <span className="mb-2 block text-sm font-semibold text-gray-700">
                  Retail store
                </span>
                <select
                  value={form.storeId}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      storeId: event.target.value,
                    }))
                  }
                  className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">Select retail store</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} ({store.code})
                      {store.branch ? ` — ${store.branch.name}` : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className="mb-2 block text-sm font-semibold text-gray-700">
                  Counter name
                </span>
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Example: Billing Counter 1"
                  maxLength={100}
                  className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </label>

              <label>
                <span className="mb-2 block text-sm font-semibold text-gray-700">
                  Counter code
                </span>
                <input
                  value={form.code}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      code: event.target.value.toUpperCase(),
                    }))
                  }
                  placeholder={counter ? "POS-001" : "Auto-generated if blank"}
                  maxLength={50}
                  className="h-12 w-full rounded-xl border border-gray-200 px-4 font-mono uppercase outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </label>

              {counter && (
                <label className="sm:col-span-2 flex cursor-pointer items-center justify-between rounded-2xl border border-gray-200 p-4">
                  <div>
                    <p className="font-semibold text-gray-900">
                      Counter is active
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Inactive counters cannot be opened for POS sales.
                    </p>
                  </div>

                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        isActive: event.target.checked,
                      }))
                    }
                    className="h-5 w-5 accent-primary-800"
                  />
                </label>
              )}
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="h-11 rounded-xl border border-gray-200 px-5 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSaving}
                className="h-11 rounded-xl bg-primary-800 px-5 font-medium text-white hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving
                  ? "Saving..."
                  : counter
                    ? "Save Counter"
                    : "Create Counter"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function userLabel(user) {
  const employee = user?.employee;
  if (employee?.fullName) {
    return `${employee.fullName}${employee.nickname ? ` (${employee.nickname})` : ""}`;
  }
  return user?.email || user?.phone || "Unnamed user";
}

function CounterUsersModal({
  counter,
  accessToken,
  onClose,
  onChanged,
}) {
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [savingUserId, setSavingUserId] = useState("");
  const [modalError, setModalError] = useState("");

  const loadAssignedUsers = useCallback(async () => {
    if (!counter) return;

    setIsLoading(true);
    try {
      const response = await getCounterUsers(counter.id, accessToken);
      setAssignedUsers(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setModalError(requestError.message || "Unable to load assigned users.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, counter]);

  const loadCandidates = useCallback(async () => {
    if (!counter) return;

    setIsSearching(true);
    try {
      const response = await searchAssignableCounterUsers(
        counter.id,
        search,
        accessToken,
      );
      setCandidates(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setModalError(requestError.message || "Unable to find eligible users.");
    } finally {
      setIsSearching(false);
    }
  }, [accessToken, counter, search]);

  useEffect(() => {
    setModalError("");
    setSearch("");
    loadAssignedUsers();
  }, [loadAssignedUsers]);

  useEffect(() => {
    const timeout = window.setTimeout(loadCandidates, 250);
    return () => window.clearTimeout(timeout);
  }, [loadCandidates]);

  if (!counter) return null;

  const assignedUserIds = new Set(assignedUsers.map((access) => access.userId));
  const availableCandidates = candidates.filter(
    (candidate) => !assignedUserIds.has(candidate.id),
  );

  const changeAccess = async (userId, action) => {
    setSavingUserId(userId);
    setModalError("");

    try {
      await action();
      await loadAssignedUsers();
      onChanged();
    } catch (requestError) {
      setModalError(requestError.message || "Unable to update counter access.");
    } finally {
      setSavingUserId("");
    }
  };

  return (
    <div className="fixed inset-0 z-[110] overflow-y-auto bg-gray-950/50 p-4 sm:p-6">
      <div className="mx-auto flex min-h-full max-w-5xl items-center">
        <section className="w-full overflow-hidden rounded-3xl bg-white shadow-2xl">
          <header className="flex items-start justify-between border-b border-gray-100 p-6">
            <div>
              <p className="text-sm text-gray-500">POS Counter access</p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                Assign users to {counter.name}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {counter.store?.name || "Store not available"}
                {counter.store?.branch ? ` · ${counter.store.branch.name}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
              aria-label="Close user access manager"
            >
              <FiX size={20} />
            </button>
          </header>

          {modalError && (
            <div className="mx-6 mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {modalError}
            </div>
          )}

          <div className="grid gap-0 lg:grid-cols-2">
            <div className="border-b border-gray-100 p-6 lg:border-b-0 lg:border-r">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-gray-900">Assigned users</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Only active assignments can open this counter.
                  </p>
                </div>
                <span className="rounded-full bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-800">
                  {assignedUsers.length}
                </span>
              </div>

              <div className="max-h-[380px] space-y-3 overflow-y-auto pr-1">
                {isLoading ? (
                  <p className="py-10 text-center text-sm text-gray-500">Loading users...</p>
                ) : assignedUsers.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                    No one is assigned yet. Search on the right to give a user POS access.
                  </div>
                ) : (
                  assignedUsers.map((access) => {
                    const isSaving = savingUserId === access.userId;
                    const isActive = access.isActive;
                    return (
                      <div key={access.userId} className="rounded-2xl border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-gray-900">{userLabel(access.user)}</p>
                            <p className="mt-1 text-sm text-gray-500">
                              {access.user?.employee?.employeeCode || "No employee code"}
                              {access.user?.email ? ` · ${access.user.email}` : ""}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-2">
                            {access.isDefault && (
                              <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-800">Default</span>
                            )}
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                              {isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {!access.isDefault && (
                            <button
                              type="button"
                              disabled={isSaving}
                              onClick={() => changeAccess(access.userId, () => updateCounterUser(counter.id, access.userId, { isActive: true, isDefault: true }, accessToken))}
                              className="rounded-lg border border-primary-200 px-3 py-2 text-xs font-semibold text-primary-800 hover:bg-primary-50 disabled:opacity-50"
                            >
                              Make default
                            </button>
                          )}
                          {isActive ? (
                            <button
                              type="button"
                              disabled={isSaving}
                              onClick={() => changeAccess(access.userId, () => deactivateCounterUser(counter.id, access.userId, accessToken))}
                              className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                            >
                              Disable access
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={isSaving}
                              onClick={() => changeAccess(access.userId, () => updateCounterUser(counter.id, access.userId, { isActive: true }, accessToken))}
                              className="inline-flex items-center gap-1 rounded-lg border border-green-200 px-3 py-2 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
                            >
                              <FiRefreshCw /> Reactivate
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-6">
              <h3 className="font-bold text-gray-900">Add a user</h3>
              <p className="mt-1 text-sm text-gray-500">
                Users must be active and have access to this counter’s branch.
              </p>
              <div className="relative mt-5">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search name, code, email, or phone..."
                  className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div className="mt-4 max-h-[310px] space-y-3 overflow-y-auto pr-1">
                {isSearching ? (
                  <p className="py-8 text-center text-sm text-gray-500">Searching eligible users...</p>
                ) : availableCandidates.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                    No eligible unassigned users found for this branch.
                  </div>
                ) : (
                  availableCandidates.map((candidate) => (
                    <div key={candidate.id} className="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 p-4">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-gray-900">{userLabel(candidate)}</p>
                        <p className="mt-1 truncate text-sm text-gray-500">
                          {candidate.employee?.employeeCode || "No employee code"}
                          {candidate.email ? ` · ${candidate.email}` : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={savingUserId === candidate.id}
                        onClick={() => changeAccess(candidate.id, () => assignCounterUser(counter.id, { userId: candidate.id, isDefault: assignedUsers.length === 0 }, accessToken))}
                        className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl bg-primary-800 px-4 text-sm font-semibold text-white hover:bg-primary-900 disabled:opacity-50"
                      >
                        <FiUserPlus /> Assign
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <footer className="flex justify-end border-t border-gray-100 p-5">
            <button type="button" onClick={onClose} className="h-11 rounded-xl border border-gray-200 px-5 font-medium text-gray-700 hover:bg-gray-50">
              Done
            </button>
          </footer>
        </section>
      </div>
    </div>
  );
}

export default function ManageCounters() {
  const { accessToken } = useAuth();

  const [counters, setCounters] = useState([]);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [selectedStoreId, setSelectedStoreId] = useState("ALL");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingCounter, setEditingCounter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accessCounter, setAccessCounter] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const [counterResponse, storeResponse] = await Promise.all([
        getAllCounters(accessToken),
        getCounterStores(accessToken),
      ]);

      setCounters(
        Array.isArray(counterResponse)
          ? counterResponse
          : counterResponse?.data || [],
      );

      setStores(
        Array.isArray(storeResponse) ? storeResponse : storeResponse?.data || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load POS counters.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredCounters = useMemo(() => {
    const query = search.trim().toLowerCase();

    return counters.filter((counter) => {
      const matchesSearch =
        !query ||
        [
          counter.counterNumber?.toString(),
          counter.code,
          counter.name,
          counter.store?.name,
          counter.store?.code,
          counter.store?.branch?.name,
        ].some((value) => value?.toLowerCase().includes(query));

      const matchesStatus =
        status === "ALL" ||
        (status === "ACTIVE" && counter.isActive) ||
        (status === "INACTIVE" && !counter.isActive);

      const matchesStore =
        selectedStoreId === "ALL" || counter.storeId === selectedStoreId;

      return matchesSearch && matchesStatus && matchesStore;
    });
  }, [counters, search, selectedStoreId, status]);
  const countersPagination = useClientPagination(filteredCounters);

  const stats = useMemo(
    () => ({
      total: counters.length,
      active: counters.filter((counter) => counter.isActive).length,
      inactive: counters.filter((counter) => !counter.isActive).length,
      assignedUsers: counters.reduce(
        (total, counter) => total + (counter._count?.userAccess || 0),
        0,
      ),
    }),
    [counters],
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCounter(null);
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    setError("");

    try {
      if (editingCounter) {
        await updateCounter(editingCounter.id, payload, accessToken);
      } else {
        await createCounter(payload, accessToken);
      }

      closeModal();
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to save POS counter.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (counter) => {
    const confirmed = window.confirm(
      `Delete "${counter.name}"?\n\nThis safely deactivates the counter and keeps all POS sales history.`,
    );

    if (!confirmed) return;

    try {
      setError("");
      await deleteCounter(counter.id, accessToken);
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete POS counter.");
    }
  };

  const toggleCounter = async (counter) => {
    try {
      setError("");

      await updateCounter(
        counter.id,
        {
          isActive: !counter.isActive,
        },
        accessToken,
      );

      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to update POS counter.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">POS / Configuration</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Manage Counters
          </h1>
          <p className="mt-2 text-gray-500">
            Create retail billing counters and control which counters are active.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingCounter(null);
            setIsModalOpen(true);
          }}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 font-medium text-white hover:bg-primary-900"
        >
          <FiPlus />
          Add Counter
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["Total Counters", stats.total, "text-gray-900"],
          ["Active Counters", stats.active, "text-green-600"],
          ["Inactive Counters", stats.inactive, "text-red-600"],
          ["Assigned Users", stats.assignedUsers, "text-primary-700"],
        ].map(([label, value, color]) => (
          <div key={label} className="rounded-3xl border border-gray-100 bg-white p-6">
            <p className="text-sm text-gray-500">{label}</p>
            <p className={`mt-2 text-3xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="grid gap-4 border-b border-gray-100 p-5 lg:grid-cols-[minmax(0,1fr)_180px_180px]">
          <div className="relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search counter, code, store, or branch..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <select
            value={selectedStoreId}
            onChange={(event) => setSelectedStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
          >
            <option value="ALL">All stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-6 py-4">Counter</th>
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Store</th>
                <th className="px-6 py-4">Branch</th>
                <th className="px-6 py-4">Assigned users</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-14 text-center text-sm text-gray-500">
                    Loading POS counters...
                  </td>
                </tr>
              ) : filteredCounters.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-14 text-center text-sm text-gray-500">
                    No POS counters found.
                  </td>
                </tr>
              ) : (
                countersPagination.pageItems.map((counter) => (
                  <tr key={counter.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                          <FiMonitor size={19} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {counter.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Counter #{counter.counterNumber}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 font-mono text-sm text-gray-700">
                      {counter.code}
                    </td>

                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                      {counter.store?.name || "—"}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-600">
                      {counter.store?.branch
                        ? `${counter.store.branch.name} (${counter.store.branch.code})`
                        : "Central / Unassigned"}
                    </td>

                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                        <FiUsers />
                        {counter._count?.userAccess || 0}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          counter.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {counter.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          title="Manage assigned users"
                          onClick={() => setAccessCounter(counter)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiUsers />
                        </button>

                        <button
                          type="button"
                          title={counter.isActive ? "Deactivate" : "Activate"}
                          onClick={() => toggleCounter(counter)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiPower />
                        </button>

                        <button
                          type="button"
                          title="Edit counter"
                          onClick={() => {
                            setEditingCounter(counter);
                            setIsModalOpen(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          title="Delete counter"
                          onClick={() => handleDelete(counter)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-red-500 hover:border-red-200 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={countersPagination} label="POS counters" />
      </div>

      <CounterModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={handleSave}
        counter={editingCounter}
        stores={stores}
        isSaving={isSaving}
      />

      <CounterUsersModal
        counter={accessCounter}
        accessToken={accessToken}
        onClose={() => setAccessCounter(null)}
        onChanged={loadData}
      />
    </div>
  );
}
